import { useEffect, useState } from 'react';
import api from '../services/api';
import { useToast } from '../context/ToastContext';

const REASON_LBL = {
  scam: '🚨 Arnaque', harassment: '⚠️ Harcèlement', fake_profile: '👤 Faux profil',
  inappropriate: '🚫 Inapproprié', spam: '📨 Spam', other: '❓ Autre',
};

const STATUS_BADGE = {
  open:           ['#fee2e2', '#991b1b', '🔴 Ouvert'],
  investigating:  ['#fef9c3', '#854d0e', '🔍 En cours'],
  resolved:       ['#dcfce7', '#166534', '✓ Résolu'],
  dismissed:      ['#f1f5f9', '#64748b', '✕ Rejeté'],
};

const fmtDate = (d) => d ? new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

export default function Moderation() {
  const [tab, setTab] = useState('reports');
  const [reports, setReports] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  const reload = () => {
    setLoading(true);
    Promise.all([
      api.get('/admin/moderation/reports').catch(() => ({ data: [] })),
      api.get('/admin/moderation/reviews').catch(() => ({ data: [] })),
    ]).then(([r1, r2]) => {
      setReports(r1.data || []);
      setReviews(r2.data || []);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { reload(); }, []);

  const updateReport = async (id, status) => {
    const note = status === 'resolved' || status === 'dismissed' ? window.prompt('Note de résolution (optionnel) :') : null;
    try {
      await api.put(`/admin/moderation/reports/${id}`, { status, resolution_note: note });
      toast?.success?.('Signalement mis à jour');
      reload();
    } catch { toast?.error?.('Erreur'); }
  };

  const toggleReview = async (id) => {
    try {
      await api.put(`/admin/moderation/reviews/${id}/toggle`);
      toast?.success?.('Avis modifié');
      reload();
    } catch { toast?.error?.('Erreur'); }
  };

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 280 }}><div className="spinner" /></div>;

  return (
    <div className="animate-fade">
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>Modération</h2>
        <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Signalements et avis utilisateurs</p>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <TabBtn active={tab === 'reports'} onClick={() => setTab('reports')} count={reports.filter(r => r.status === 'open').length} label="🚩 Signalements" />
        <TabBtn active={tab === 'reviews'} onClick={() => setTab('reviews')} count={reviews.length} label="⭐ Avis" />
      </div>

      {tab === 'reports' ? (
        <div className="card">
          <div className="card-body">
            {reports.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>Aucun signalement</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
                      <th style={th}>Date</th>
                      <th style={th}>Signalé par</th>
                      <th style={th}>Cible</th>
                      <th style={th}>Motif</th>
                      <th style={th}>Description</th>
                      <th style={th}>Statut</th>
                      <th style={th}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reports.map(r => {
                      const [bg, color, label] = STATUS_BADGE[r.status] || ['#f1f5f9', '#64748b', r.status];
                      return (
                        <tr key={r.id} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={td}>{fmtDate(r.created_at)}</td>
                          <td style={td}>
                            <div style={{ fontWeight: 600 }}>{r.reporter_name}</div>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{r.reporter_phone}</div>
                          </td>
                          <td style={td}>{r.target_type} #{r.target_id}</td>
                          <td style={td}>{REASON_LBL[r.reason] || r.reason}</td>
                          <td style={{ ...td, maxWidth: 260, fontSize: 12 }}>{r.description || '—'}</td>
                          <td style={td}><span style={{ background: bg, color, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>{label}</span></td>
                          <td style={td}>
                            {r.status === 'open' && (
                              <>
                                <button onClick={() => updateReport(r.id, 'investigating')} style={btnSm}>🔍</button>
                                <button onClick={() => updateReport(r.id, 'resolved')} style={btnSm}>✓</button>
                                <button onClick={() => updateReport(r.id, 'dismissed')} style={btnSm}>✕</button>
                              </>
                            )}
                            {r.status === 'investigating' && (
                              <>
                                <button onClick={() => updateReport(r.id, 'resolved')} style={btnSm}>✓ Résoudre</button>
                                <button onClick={() => updateReport(r.id, 'dismissed')} style={btnSm}>✕ Rejeter</button>
                              </>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-body">
            {reviews.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>Aucun avis</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
                      <th style={th}>Date</th>
                      <th style={th}>Auteur</th>
                      <th style={th}>Concernant</th>
                      <th style={th}>Service</th>
                      <th style={{ ...th, textAlign: 'center' }}>Note</th>
                      <th style={th}>Commentaire</th>
                      <th style={th}>Visibilité</th>
                      <th style={th}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reviews.map(r => (
                      <tr key={r.id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={td}>{fmtDate(r.created_at)}</td>
                        <td style={td}>
                          <div style={{ fontWeight: 600 }}>{r.reviewer_name}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{r.reviewer_phone}</div>
                        </td>
                        <td style={td}>{r.reviewed_name}</td>
                        <td style={{ ...td, maxWidth: 180 }}>{r.service_title || '—'}</td>
                        <td style={{ ...td, textAlign: 'center', fontWeight: 700, fontSize: 16 }}>
                          {'⭐'.repeat(r.rating)}
                        </td>
                        <td style={{ ...td, maxWidth: 260, fontSize: 12 }}>{r.comment || '—'}</td>
                        <td style={td}>
                          <span style={{ background: r.is_hidden ? '#fee2e2' : '#dcfce7', color: r.is_hidden ? '#991b1b' : '#166534', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>
                            {r.is_hidden ? '🙈 Masqué' : '✓ Visible'}
                          </span>
                        </td>
                        <td style={td}>
                          <button onClick={() => toggleReview(r.id)} style={btnSm}>
                            {r.is_hidden ? '👁 Afficher' : '🙈 Masquer'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const TabBtn = ({ active, onClick, count, label }) => (
  <button onClick={onClick} style={{
    padding: '10px 18px', borderRadius: 10, border: 'none', cursor: 'pointer',
    background: active ? 'var(--primary)' : 'var(--surface)', color: active ? '#fff' : 'var(--text-primary)',
    fontSize: 14, fontWeight: 600, borderWidth: 1, borderStyle: 'solid', borderColor: active ? 'var(--primary)' : 'var(--border)',
  }}>
    {label} {count > 0 && <span style={{ marginLeft: 6, background: active ? 'rgba(255,255,255,0.25)' : 'var(--primary-light)', padding: '1px 8px', borderRadius: 10, fontSize: 11 }}>{count}</span>}
  </button>
);

const th = { padding: '10px 8px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text-muted)' };
const td = { padding: '12px 8px', color: 'var(--text-primary)', verticalAlign: 'top' };
const btnSm = { background: 'var(--surface-2)', border: '1px solid var(--border)', padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12, marginRight: 4 };
